import React, { useState, useRef } from 'react';
import { 
  X, 
  Building2, 
  Upload, 
  Trash2, 
  Check, 
  Stamp, 
  Image as ImageIcon,
  Sparkles,
  RotateCcw
} from 'lucide-react';

const CompanySealModal = ({ isOpen, onClose, currentSeal, onSave }) => {
  const [sealType, setSealType] = useState(currentSeal?.type || 'CUSTOM_IMAGE'); // 'CUSTOM_IMAGE' | 'AUTO_STAMP'
  const [companyName, setCompanyName] = useState(currentSeal?.companyName || '주식회사 클로우플레이크');
  const [representativeName, setRepresentativeName] = useState(currentSeal?.representativeName || '대표이사 이용관');
  const [address, setAddress] = useState(currentSeal?.address || '서울시 강서구 양천로 354 옥천빌딩 9층 클로우플레이크 본사');
  const [sealText, setSealText] = useState(currentSeal?.sealText || '클로우\n플레이크\n대표이사');
  const [shape, setShape] = useState(currentSeal?.shape || 'CIRCLE'); // 'CIRCLE' | 'OVAL' | 'SQUARE'
  const [customImageUrl, setCustomImageUrl] = useState(currentSeal?.imageUrl || '/clouflake.png');
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleResetToDefaultSeal = () => {
    setCustomImageUrl('/clouflake.png');
    setSealType('CUSTOM_IMAGE');
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert('직인 이미지 파일은 2MB 이하만 업로드 가능합니다.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (uploadEvent) => {
      setCustomImageUrl(uploadEvent.target.result);
      setSealType('CUSTOM_IMAGE');
    };
    reader.readAsDataURL(file);
  };

  const handleSave = (e) => {
    e.preventDefault();
    const updated = {
      type: sealType,
      companyName: companyName.trim() || '주식회사 클로우플레이크',
      representativeName: representativeName.trim() || '대표이사 이용관',
      address: address.trim() || '서울시 강서구 양천로 354 옥천빌딩 9층 클로우플레이크 본사',
      sealText: sealText.trim() || '클로우\n플레이크\n대표이사',
      shape,
      imageUrl: sealType === 'CUSTOM_IMAGE' ? (customImageUrl || '/clouflake.png') : null,
      updatedAt: new Date().toISOString()
    };
    onSave(updated);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-3 md:p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl max-w-xl w-full flex flex-col shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shadow-md">
              <Stamp className="w-5 h-5" />
            </div>
            <div className="text-left">
              <h2 className="text-base md:text-lg font-bold">회사 법인직인(인감) 관리</h2>
              <p className="text-xs text-slate-300 mt-0.5">전자계약 및 사직서 등 모든 법적 서류에 날인되는 회사 직인을 설정합니다.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-5 text-left text-xs text-slate-800">
          
          {/* Company Basic Information */}
          <div className="space-y-3 bg-slate-50 p-4 rounded-2xl border border-slate-200">
            <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
              <Building2 className="w-4 h-4 text-indigo-600" />
              계약서 명기 회사 정보
            </span>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold text-slate-600 block mb-1">회사 상호 (법인명)</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-bold text-slate-900 text-xs focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="text-[11px] font-bold text-slate-600 block mb-1">대표자 직책 및 성명</label>
                <input
                  type="text"
                  value={representativeName}
                  onChange={e => setRepresentativeName(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-bold text-slate-900 text-xs focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              <div className="sm:col-span-2">
                <label className="text-[11px] font-bold text-slate-600 block mb-1">사업장 주소</label>
                <input
                  type="text"
                  value={address}
                  onChange={e => setAddress(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-slate-900 text-xs focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
            </div>
          </div>

          {/* Seal Mode Selection */}
          <div className="space-y-3">
            <label className="text-xs font-bold text-slate-700 block">직인 날인 방식 선택</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSealType('CUSTOM_IMAGE')}
                className={`p-3.5 rounded-2xl border text-left transition-all cursor-pointer flex items-center gap-3 ${
                  sealType === 'CUSTOM_IMAGE'
                    ? 'border-indigo-600 bg-indigo-50/70 text-indigo-900 ring-2 ring-indigo-500/20 font-bold'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                <ImageIcon className="w-5 h-5 text-indigo-600 shrink-0" />
                <div>
                  <div className="font-extrabold text-xs">직인 이미지 업로드</div>
                  <div className="text-[10px] text-slate-500 font-normal mt-0.5">실제 법인도장 스캔/투명 PNG</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSealType('AUTO_STAMP')}
                className={`p-3.5 rounded-2xl border text-left transition-all cursor-pointer flex items-center gap-3 ${
                  sealType === 'AUTO_STAMP'
                    ? 'border-indigo-600 bg-indigo-50/70 text-indigo-900 ring-2 ring-indigo-500/20 font-bold'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Stamp className="w-5 h-5 text-rose-600 shrink-0" />
                <div>
                  <div className="font-extrabold text-xs">전자 도장 자동 생성</div>
                  <div className="text-[10px] text-slate-500 font-normal mt-0.5">원형/정방형 표준 인감 각인</div>
                </div>
              </button>
            </div>
          </div>

          {/* Option 1: Custom Image Upload */}
          {sealType === 'CUSTOM_IMAGE' && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
              <span className="text-xs font-bold text-slate-800 block">직인 이미지 파일 등록</span>
              
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="w-24 h-24 rounded-2xl bg-white border-2 border-dashed border-slate-300 flex items-center justify-center p-2 overflow-hidden shrink-0 shadow-2xs">
                  {customImageUrl ? (
                    <img src={customImageUrl} alt="직인 미리보기" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-[10px] text-slate-400 text-center">이미지 없음</span>
                  )}
                </div>

                <div className="space-y-2 flex-1 text-left">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/png,image/jpeg,image/svg+xml"
                    className="hidden"
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 cursor-pointer shadow-xs"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      직인 이미지 선택 (PNG/JPG)
                    </button>
                    <button
                      type="button"
                      onClick={handleResetToDefaultSeal}
                      className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs flex items-center gap-1 cursor-pointer border border-slate-200"
                      title="회사 표준 직인(clouflake.png)으로 재설정"
                    >
                      <RotateCcw className="w-3.5 h-3.5 text-indigo-600" />
                      기본 직인 복원
                    </button>
                    {customImageUrl && customImageUrl !== '/clouflake.png' && (
                      <button
                        type="button"
                        onClick={() => setCustomImageUrl('/clouflake.png')}
                        className="p-2 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-xl cursor-pointer"
                        title="기본 직인으로 변경"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500 leading-tight">
                    배경이 투명한 PNG 파일을 권장하며, 계약서 하단 회사 대표 날인 위치에 실제 도장 크기로 자동 인쇄됩니다.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Option 2: Digital Stamp Generator */}
          {sealType === 'AUTO_STAMP' && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
              <span className="text-xs font-bold text-slate-800 block">전자 직인 각인 설정</span>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-600 block mb-1">도장 모양</label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {[
                      { id: 'CIRCLE', label: '원형' },
                      { id: 'OVAL', label: '타원형' },
                      { id: 'SQUARE', label: '정방형(직인)' }
                    ].map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setShape(s.id)}
                        className={`py-1.5 px-2 rounded-xl text-xs font-bold border cursor-pointer transition-all ${
                          shape === s.id
                            ? 'bg-rose-600 text-white border-rose-600 shadow-xs'
                            : 'bg-white text-slate-700 border-slate-200'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-600 block mb-1">도장 각인 문구 (줄바꿈 구분)</label>
                  <textarea
                    rows={2}
                    value={sealText}
                    onChange={e => setSealText(e.target.value)}
                    className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-rose-700 focus:ring-2 focus:ring-indigo-500 resize-none font-mono"
                    placeholder="클로우&#10;플레이크&#10;대표이사"
                  />
                </div>
              </div>

              {/* Real-time Preview */}
              <div className="pt-2 border-t border-slate-200 flex items-center justify-between">
                <span className="text-[11px] font-bold text-slate-500">실시간 직인 날인 미리보기:</span>
                <div className="flex items-center gap-3 bg-white p-2.5 rounded-xl border border-slate-200 shadow-2xs">
                  {shape === 'CIRCLE' && (
                    <div className="w-14 h-14 rounded-full border-2 border-rose-600 flex items-center justify-center text-rose-600 font-bold text-[10px] text-center leading-tight bg-rose-50/40 shadow-xs rotate-[-6deg] whitespace-pre-line">
                      {sealText}
                    </div>
                  )}
                  {shape === 'OVAL' && (
                    <div className="w-16 h-14 rounded-[40%] border-2 border-rose-600 flex items-center justify-center text-rose-600 font-bold text-[10px] text-center leading-tight bg-rose-50/40 shadow-xs rotate-[-4deg] whitespace-pre-line">
                      {sealText}
                    </div>
                  )}
                  {shape === 'SQUARE' && (
                    <div className="w-14 h-14 border-2 border-rose-600 flex items-center justify-center text-rose-600 font-bold text-[10px] text-center leading-tight bg-rose-50/40 shadow-xs rotate-[-3deg] whitespace-pre-line">
                      {sealText}
                    </div>
                  )}
                  <div className="text-[10px] text-slate-400">
                    <span className="font-bold text-slate-700 block">계약 체결 시 자동 날인</span>
                    법적 효력 완비
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer Buttons */}
          <div className="pt-3 border-t border-slate-200 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs cursor-pointer"
            >
              취소
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 shadow-md active:scale-98 cursor-pointer"
            >
              <Check className="w-4 h-4" />
              직인 설정 저장하기
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanySealModal;
